import { Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import { useMemo, useState } from 'react';
import { PackingOperation, PackingListItem } from '../schema';
import { queryItemTypesWithLocations } from '../schema';
import { db } from '../instant';
import ConfirmationChecklistRow from './ConfirmationChecklistRow';

interface Props {
    event: PackingOperation['targetEvents'][number];
    operation: PackingOperation;
    isReadOnly?: boolean;
}

export default function ConfirmationChecklist({ event, operation, isReadOnly = false }: Props) {
    const { data: itemTypesData, isLoading } = queryItemTypesWithLocations();
    const packingListItems = useMemo(() => (event.packingListItems || []) as PackingListItem[], [event.packingListItems]);

    /** Same ordering as EventInventoryPackingList: trailers first (by name), then by category then name */
    const sortedPackingListItems = useMemo(() => {
        const trailers: PackingListItem[] = [];
        const other: PackingListItem[] = [];
        for (const item of packingListItems) {
            const categoryName = item.itemType?.category?.name || '';
            if (categoryName === 'Trailers') {
                trailers.push(item);
            } else {
                other.push(item);
            }
        }
        trailers.sort((a, b) => (a.itemType?.name ?? '').localeCompare(b.itemType?.name ?? '', undefined, { sensitivity: 'base' }));
        other.sort((a, b) => {
            const catA = a.itemType?.category?.name ?? '';
            const catB = b.itemType?.category?.name ?? '';
            const cmp = catA.localeCompare(catB, undefined, { sensitivity: 'base' });
            if (cmp !== 0) return cmp;
            return (a.itemType?.name ?? '').localeCompare(b.itemType?.name ?? '', undefined, { sensitivity: 'base' });
        });
        return [...trailers, ...other];
    }, [packingListItems]);

    const [bulkUpdating, setBulkUpdating] = useState(false);

    const readOnly = isReadOnly || operation.status === 'completed';
    const unconfirmedCount = useMemo(() => packingListItems.filter(item => !item.confirmed).length, [packingListItems]);
    const confirmedCount = useMemo(() => packingListItems.filter(item => item.confirmed).length, [packingListItems]);

    const handleCheckAll = async () => {
        if (readOnly || bulkUpdating || unconfirmedCount === 0) return;
        setBulkUpdating(true);
        try {
            await db.transact(
                packingListItems.filter(item => !item.confirmed).map(item => db.tx.packingListItems[item.id].update({ confirmed: true }))
            );
        } finally {
            setBulkUpdating(false);
        }
    };

    const handleUncheckAll = async () => {
        if (readOnly || bulkUpdating || confirmedCount === 0) return;
        setBulkUpdating(true);
        try {
            await db.transact(
                packingListItems.filter(item => item.confirmed).map(item => db.tx.packingListItems[item.id].update({ confirmed: false }))
            );
        } finally {
            setBulkUpdating(false);
        }
    };

    // Extract all itemLocations from itemTypes for status calculation
    const allItemLocations = useMemo(() => {
        const locations: Array<{
            id: string;
            quantity: number;
            itemType?: { id: string } | null;
            event?: { id: string } | null;
        }> = [];

        const itemTypes = itemTypesData?.itemTypes || [];
        for (const itemType of itemTypes) {
            const itemLocations = (itemType as any).itemLocations || [];
            for (const location of itemLocations) {
                locations.push({
                    id: location.id,
                    quantity: location.quantity || 0,
                    itemType: { id: itemType.id },
                    event: location.event ? { id: location.event.id } : null,
                });
            }
        }
        return locations;
    }, [itemTypesData]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" padding={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (packingListItems.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                    No items in packing list for this event
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DoneAllIcon />}
                    onClick={handleCheckAll}
                    disabled={readOnly || bulkUpdating || unconfirmedCount === 0}
                >
                    Check all
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RemoveDoneIcon />}
                    onClick={handleUncheckAll}
                    disabled={readOnly || bulkUpdating || confirmedCount === 0}
                >
                    Uncheck all
                </Button>
            </Box>
            <Paper
                sx={{
                    overflowX: 'auto',
                    width: '100%',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    borderRadius: '8px',
                    border: '1px solid rgba(0,0,0,0.06)',
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">Confirmed</TableCell>
                            <TableCell>Photo</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Required</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Location</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedPackingListItems.map(item => (
                            <ConfirmationChecklistRow
                                key={item.id}
                                packingListItem={item}
                                eventId={event.id}
                                eventName={event.name}
                                allItemLocations={allItemLocations}
                                isReadOnly={isReadOnly || operation.status === 'completed'}
                            />
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
}
